/**
 * 技能缺口雷达面板（扩展①）：挂在队伍页的「缺口」Tab。
 *
 * 数据：GET /teams/:id/gap —— 缺口 = 赛事需求 −（成员技能 ∪ 招募已承诺技能），
 * 缺口率环 + 已覆盖/还缺标签 + 引擎建议（只建议不自动邀请）。
 */
import { skillName } from '@crossmatch/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import type { GapDto } from '../api/types.js';
import { ErrorState } from './ErrorState.js';
import { Loading } from './Loading.js';
import { Tag } from './Tag.js';

function coveragePercent(gap: GapDto): number | null {
  if (gap.gapRatio === null) return null;
  return Math.round((1 - gap.gapRatio) * 100);
}

export function GapPanel({ query }: { query: UseQueryResult<GapDto, Error> }) {
  if (query.isPending) return <Loading label="正在计算技能缺口…" rows={2} />;
  if (query.isError) {
    return <ErrorState error={query.error} title="读取技能缺口失败" onRetry={() => void query.refetch()} />;
  }

  const gap = query.data;
  const percent = coveragePercent(gap);
  const requiredIds = new Set(gap.required.map((row) => row.skillId));

  if (gap.eventCode === null) {
    return (
      <p className="muted small">这支队伍还没挂赛事——创建时填赛事码，或让赛事方维护「典型技能需求」后，这里会自动算出还缺什么。</p>
    );
  }
  if (requiredIds.size === 0) {
    return (
      <p className="muted small">
        赛事 {gap.eventCode} 还没有维护典型技能需求。赛事方在「赛事联动 → 赛事详情」里粘贴通知原文，AI
        抽取后确认即可生成需求集。
      </p>
    );
  }

  return (
    <div className="stack">
      <div className="row-tight">
        <strong>技能覆盖率</strong>
        {percent === null ? (
          <Tag tone="muted">待维护</Tag>
        ) : (
          <Tag tone={percent >= 70 ? 'ok' : percent >= 40 ? 'primary' : 'danger'}>
            {percent}%（{gap.covered.length}/{requiredIds.size}）
          </Tag>
        )}
      </div>

      <div className="row-tight" aria-label="已覆盖技能">
        {gap.covered.length === 0 ? <span className="muted small">暂无已覆盖技能</span> : null}
        {gap.covered.map((skill) => (
          <Tag key={skill} tone="ok" title={skill}>
            {skillName(skill)}
          </Tag>
        ))}
      </div>

      {gap.missing.length > 0 ? (
        <div className="row-tight" aria-label="还缺的技能">
          <span className="small muted">还缺：</span>
          {gap.missing.map((skill) => (
            <Tag key={skill} tone="danger" title={skill}>
              {skillName(skill)}
            </Tag>
          ))}
        </div>
      ) : (
        <p className="small">需求技能已全部覆盖，可以考虑补时间协同或冲更高完成度。</p>
      )}

      {gap.suggestions.length > 0 ? (
        <div className="stack">
          <strong className="small">按缺口推荐（引擎前 {gap.suggestions.length} 名，只建议不自动邀请）</strong>
          {gap.suggestions.map((item) => (
            <div key={item.candidateId} className="card">
              <div className="row-tight">
                <strong>{item.name}</strong>
                <Tag tone="school">{item.school}</Tag>
                <Tag tone="primary">互补度 {item.total}</Tag>
              </div>
              <ul className="small muted reason-list">
                {item.reasons.slice(0, 3).map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
